/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Link,
  useTheme,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import CsvDownloadButton from "react-json-to-csv";
import { currencyFormatter } from "../../constants";
import { canDownloadModel } from "../../utils";
import { outputsConstants } from "./helpers";
import {
  shouldRender,
  usesSchema,
  getDecimalPlaces,
  getFieldDefinition,
} from "../../providers/FieldConfigUtils";
import { useJsonData } from "../../providers/FieldConfigProvider.jsx";

import "./Outputs.css";
import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";
import { Box } from "@mui/material";
import useCalculatorOutputs from "../../api/useCalculatorOutputs";
import callApiJson from "../../api/callApi";
import NewSystemDesignModal from "./NewSystemDesignModal";

function Outputs({
  outputs,
  action,
  businessName,
  projectId,
  quoteData: selectedQuoteData,
  systemDesignData,
  selectedSystemDesign,
  opportunityData,
  handleSave,
  selectedFinancingType,
  showOutputs,
  onOutputsChange,
  prevFormData,
  isView,
  calculatorVersion,
  opportunityId,
  outputDestination,
  isSDSales,
  onNewSdCreated,
  outputsSd,
}: {
  outputs: Array<Record<string, any>>;
  action: string;
  businessName: string;
  projectId: string;
  quoteData: any;
  systemDesignData: any;
  selectedSystemDesign: string;
  opportunityData: any;
  handleSave: () => void;
  selectedFinancingType: string;
  showOutputs: boolean;
  onOutputsChange: (outputs: Array<Record<string, any>>) => void;
  isView: boolean;
  calculatorVersion: string;
  opportunityId: string;
  outputDestination: any;
  prevFormData: any;
  isSDSales: boolean;
  onNewSdCreated: (sd: { id: string; name: string }) => Promise<void>;
  outputsSd: { id: string; name: string } | null;
}) {
  const theme = useTheme();
  const { data: prevOutputs, isLoading: loadingOutputs } = useCalculatorOutputs(
    calculatorVersion,
    isView,
    opportunityId + selectedSystemDesign,
  );
  const [tab, setTab] = useState("1");
  const [newSdModalOpen, setNewSdModalOpen] = useState(false);
  const environment = process.env.REACT_APP_ENVIRONMENT;
  const sunrockConfig = useJsonData();

  // Extract raw epc_total_nom_sum before renderOutputs mutates the output objects
  const optimizedEpcTotalNomSum = useMemo(() => {
    if (!outputs || outputs.length === 0) return null;
    const found = outputs.find(
      (o) => o.data_variable_element === "epc_total_nom_sum",
    );
    if (!found || found.value === undefined || found.value === null) return null;
    const parsed = parseFloat(found.value);
    return isNaN(parsed) ? null : parsed;
  }, [outputs]);

  useEffect(() => {
    if (!loadingOutputs && prevOutputs && prevOutputs?.length > 0 && isView) {
      onOutputsChange(prevOutputs);
    }
  }, [prevOutputs, loadingOutputs]);

  function formatValue(output_field_dict) {
    const outputSettings = outputsConstants.find(
      (x) =>
        x.data_variable_element === output_field_dict.data_variable_element,
    );
    let isNeg = false;

    if (
      output_field_dict &&
      output_field_dict.value !== null &&
      outputSettings &&
      output_field_dict.value !== undefined
    ) {
      if (parseFloat(output_field_dict.value) < 0) {
        isNeg = true;
      }
      if (outputSettings.value_type?.toLowerCase().includes("currency")) {
        const real_value_elements = [
          "epc_total_per_watt_sd",
          "epc_bess_per_kwh_sd",
          "epc_solar_per_watt_sd",
        ];
        if (
          real_value_elements.includes(output_field_dict.data_variable_element)
        ) {
          output_field_dict.real_value = parseFloat(output_field_dict.value);
        }
        output_field_dict["value"] = currencyFormatter(output_field_dict.value);
      } else if (outputSettings.value_type?.toLowerCase().includes("percent")) {
        const percentDecimalPlaces = getDecimalPlaces(outputSettings.value_type);
        if (Math.abs(parseFloat(output_field_dict.value)) < 1) {
          output_field_dict["value"] =
            (parseFloat(output_field_dict.value) * 100).toFixed(
              percentDecimalPlaces,
            ) + "%";
        } else {
          output_field_dict["value"] =
            parseFloat(output_field_dict.value).toFixed(percentDecimalPlaces) +
            "%";
        }
      } else if (outputSettings.value_type?.toLowerCase().includes("decimal")) {
        if (
          output_field_dict.data_variable_element === "epc_total_per_watt_sd"
        ) {
          output_field_dict.real_value = parseFloat(output_field_dict.value);
        }
        const decimalPlaces = getDecimalPlaces(outputSettings.value_type);
        output_field_dict.value = parseFloat(
          output_field_dict.value,
        ).toLocaleString("en-US", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
      } else if (
        [
          "storage_incentive_upfront_amount",
          "storage_incentive_perf_amount",
        ].includes(output_field_dict.data_variable_element)
      ) {
        output_field_dict["value"] = output_field_dict.value
          .split(",")
          .map((val) => {
            const num = parseFloat(val);
            return isNaN(num) ? val : currencyFormatter(num);
          })
          .join(",");
      } else {
        output_field_dict["value"] = output_field_dict.value.toString();
      }
      if (isNeg) {
        // format negative with brackets
        output_field_dict["value"] =
          "(" + output_field_dict["value"].substring(1) + ")";
      }
    }
  }

  const handleDownloadAWSClicked = async () => {
    let url: string;
    if (outputDestination === undefined || action === "view") {
      url = "/api/model?quote_id=" + selectedQuoteData.Id;
    } else {
      const filename = outputDestination.replace(/^.*[\\/]/, "");
      url = "/api/model?filename=" + filename;
    }
    const responseJson = await callApiJson(url);
    if (responseJson.status == "success") {
      const responseUrl = responseJson.url;
      window.open(responseUrl);
    } else {
      console.log(responseJson.error);
    }
  };

  const formatOutput = (output) => {
    // Review this
    //   "opp_maintain_cost_escal__c": 50.0,
    if (usesSchema(sunrockConfig, output.data_variable_element)) {
      if (!output.value) {
        return "";
      }

      // if configured as escalator, get label.
      const fieldDefinition = getFieldDefinition(
        sunrockConfig,
        output.data_variable_element,
      );
      if (fieldDefinition.isEscalator) {
        return (parseFloat(output.value) * 100).toFixed(2).toString() + "%";
      }
    }

    if (output.data_variable_element === "storage_incentive_program") {
      const storage_incentive_program_mapping = {
        DSGS: "Demand Side Grid Support",
        SGIP: "Self-Generation Incentive Program",
        CPEC: "Clean Peak Energy Standard (CPEC)",
        CSRP: "Commercial System Relief Program (CSRP)",
        MACS: "MA Connected Solutions",
        FCM: "ISO-NE Forward Capacity Market (FCM)",
        CTGESS: "CT Greenbank Energy Storage Solutions",
        RICS: "RI Connected Solutions",
        NYSERDARSI: "NYSERDA Retail Storage Incentive",
        DLRP: "Distribution Load Relief Program (DLRP)",
        VDER: "Value of Distributed Energy Resources (VDER)",
      };
      if (output.value) {
        const programs = output.value.split(",");
        const mappedPrograms = programs.map(
          (program) => storage_incentive_program_mapping[program] || program,
        );
        return mappedPrograms.join(", ");
      } else {
        return "";
      }
    }

    return output.value;
  };

  const renderOutputs = useMemo(() => {
    const result: ReactJSXElement[] = [];
    if (outputs && outputs.length > 0) {
      let counter = 0;

      // calculate per watt values
      const pv_size_dc_kw_sd =
        (systemDesignData?.pv_size_dc_kw_sd__c || 0) * 1000;

      const sr_trans_legal_fees_nom =
        outputs.find(
          (x) => x.data_variable_element === "sr_trans_legal_fees_nom",
        )?.value || 0;
      const referral_fees_total_nom =
        outputs.find(
          (x) => x.data_variable_element === "referral_fees_total_nom",
        )?.value || 0;
      const dev_only_expense_budget_total_nom =
        outputs.find(
          (x) =>
            x.data_variable_element === "dev_only_expense_budget_total_nom",
        )?.value || 0;

      // add these per_watt fields to outputs for displaying only
      outputs = [
        ...outputs,
        {
          data_variable_element: "sr_trans_legal_fees_per_watt",
          value: sr_trans_legal_fees_nom / pv_size_dc_kw_sd || 0,
        },
        {
          data_variable_element: "referral_fees_total_per_watt",
          value: referral_fees_total_nom / pv_size_dc_kw_sd || 0,
        },
        {
          data_variable_element: "dev_only_expense_budget_total_per_watt",
          value: dev_only_expense_budget_total_nom / pv_size_dc_kw_sd || 0,
        },
      ];

      // Check if "project_type" exists in outputs, if not, add it
      const project_type = systemDesignData?.project_type__c || "Solar-only";

      const hasProjectType = outputs.some(
        (output) => output.data_variable_element === "project_type",
      );
      if (!hasProjectType) {
        outputs = [
          ...outputs,
          {
            data_variable_element: "project_type",
            value: project_type,
          },
        ];
      }

      // site_lease_initial_term isn't a real Excel model cell (it's computed
      // in backend post-processing), so it's missing from outputs whenever
      // they come from the blank template (e.g. viewing a saved quote)
      // rather than a live calculation. Fall back to the value already
      // saved on the quote record whenever it's absent or uncomputed.
      const siteLeaseInitialTermOutput = outputs.find(
        (output) => output.data_variable_element === "site_lease_initial_term",
      );
      if (!siteLeaseInitialTermOutput || siteLeaseInitialTermOutput.value == null) {
        const siteLeaseInitialTermValue =
          selectedQuoteData?.site_lease_initial_term__c ?? null;
        if (siteLeaseInitialTermOutput) {
          siteLeaseInitialTermOutput.value = siteLeaseInitialTermValue;
        } else {
          outputs = [
            ...outputs,
            {
              data_variable_element: "site_lease_initial_term",
              value: siteLeaseInitialTermValue,
            },
          ];
        }
      }

      let outputsToDisplay = outputs.map((x) => {
        if (
          x.data_variable_element == "epc_total_per_watt_sd" &&
          (!x.value || isNaN(parseFloat(x.value)))
        ) {
          x.value = systemDesignData.epc_total_per_watt_sd__c;
        }
        if (x.data_variable_element == "ix_delivery_point" && !x.value) {
          x.value = systemDesignData.ix_delivery_point__c?.trim() || "BTM";
        }
        if (
          x.data_variable_element == "epc_total_nom_sum" &&
          (!x.value || isNaN(parseFloat(x.value)))
        ) {
          x.value = systemDesignData.epc_total_nom_sum__c;
        }
        if (
          x.data_variable_element == "epc_bess_per_kwh_sd" &&
          (!x.value || isNaN(parseFloat(x.value)))
        ) {
          x.value = systemDesignData.epc_bess_per_kwh_sd__c;
        }
        if (
          [
            "storage_incentive_upfront_amount",
            "storage_incentive_perf_amount",
          ].includes(x.data_variable_element) &&
          x.value
        ) {
          x.value = x.value
            .split(",")
            .map((val) => {
              const num = parseFloat(val);
              return isNaN(num) ? "N/A" : num.toFixed(2);
            })
            .join(",");
        }
        if (
          ["storage_incentive_perf_term"].includes(x.data_variable_element) &&
          x.value
        ) {
          x.value = x.value
            .split(",")
            .map((val) => {
              const num = parseInt(val);
              return isNaN(num) ? "N/A" : num.toString();
            })
            .join(",");
        }

        const outputSettings = outputsConstants.find(
          (y) => y.data_variable_element === x.data_variable_element,
        );
        if (outputSettings) {
          x.ui_name = outputSettings.ui_name;
          x.pw_system_quote_and_sd_order =
            outputSettings.pw_system_quote_and_sd_order;
          x.pw_quote_output_only_order =
            outputSettings.pw_quote_output_only_order;
          x.root = outputSettings.root;
        }
        return x;
      });

      // quote + system design flow
      // quote only flow no longer applicable
      if (systemDesignData && systemDesignData && selectedSystemDesign) {
        outputsToDisplay = outputs.filter((x) => {
          const outputSettings = outputsConstants.find(
            (y) => y.data_variable_element === x.data_variable_element,
          );
          return (
            outputSettings?.default_ui_visibility_pw_quote_and_sd_output ===
            "Yes"
          );
        });
        outputsToDisplay = outputsToDisplay.filter(
          (x) => x.pw_system_quote_and_sd_order !== null,
        );

        outputsToDisplay = outputsToDisplay.sort(
          (a, b) =>
            a.pw_system_quote_and_sd_order - b.pw_system_quote_and_sd_order,
        );
        // Schema, filter by visibility
        // outputsToDisplay = outputsToDisplay.filter(x =>
        // !usesSchema(sunrockConfig, x.data_variable_element) || shouldRender(sunrockConfig, x.data_variable_element, opportunityData, systemDesignData)
        // )

        let salesforceLink =
          "https://sunrock.lightning.force.com/lightning/r/System_Design__c";
        if (environment === "sandbox") {
          salesforceLink =
            "https://sunrock--partial.sandbox.lightning.force.com/lightning/r/System_Design__c";
        }
        result.push(
          <TableContainer
            component={Paper}
            sx={{
              "& .MuiTableCell-root": {
                backgroundColor: theme.palette.action.hover,
                borderRadius: 0,
              },
            }}
          >
            <Table aria-label="system design table">
              <TableBody>
                <TableRow>
                  <TableCell>System Design</TableCell>
                  <TableCell align="right">
                    <Link
                      href={`${salesforceLink}/${systemDesignData.Id}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Link
                    </Link>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>,
        );
      }

      if (selectedFinancingType) {
        if (selectedFinancingType === "Lease") {
          outputsToDisplay = outputsToDisplay.filter(
            (x) =>
              x["root"] === null ||
              (x["root"] !== null && x["root"].includes("Lease")),
          );
        } else if (selectedFinancingType === "PPA") {
          outputsToDisplay = outputsToDisplay.filter(
            (x) =>
              x["root"] === null ||
              (x["root"] !== null && x["root"].includes("PPA")),
          );
        } else if (selectedFinancingType === "ESA") {
          outputsToDisplay = outputsToDisplay.filter(
            (x) =>
              x["root"] === null ||
              (x["root"] !== null && x["root"].includes("ESA")),
          );
        } else {
          outputsToDisplay = outputsToDisplay.filter((x) => x["root"] === null);
        }
      }

      // HACK
      // Get Solver and remove it from normal disply, this we'll keep it at the top
      // with the other hard-coded-ordered elements (Offtaker and Project Id)
      const solver = outputsToDisplay.find(
        (x) => x["data_variable_element"] === "solver",
      );
      if (solver) {
        outputsToDisplay = outputsToDisplay.filter(
          (x) => x["data_variable_element"] !== "solver",
        );
        if (action === "view") {
          solver.value =
            selectedQuoteData[solver.data_variable_element + "__c"];
        }

        result.push(
          <TableContainer component={Paper}>
            <Table aria-label="solver table">
              <TableBody>
                <TableRow>
                  <TableCell>Solver</TableCell>
                  <TableCell align="right">{solver.value}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>,
        );
      }

      result.push(
        <TableContainer
          component={Paper}
          sx={{
            "& .MuiTableCell-root": {
              backgroundColor: theme.palette.action.hover,
              borderRadius: 0,
            },
          }}
        >
          <Table aria-label="offtaker table">
            <TableBody>
              <TableRow>
                <TableCell>Offtaker</TableCell>
                <TableCell align="right">{businessName}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>,
      );
      result.push(
        <TableContainer component={Paper}>
          <Table aria-label="project id table">
            <TableBody>
              <TableRow>
                <TableCell>Project Id</TableCell>
                <TableCell align="right">{projectId}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>,
      );

      for (let i = 0; i < outputsToDisplay?.length; i++) {
        const outputSettings = outputsConstants.find(
          (x) =>
            x.data_variable_element ===
            outputsToDisplay[i].data_variable_element,
        );
        if (
          opportunityData &&
          outputSettings &&
          outputSettings.source?.includes("Opportunity")
        ) {
          outputsToDisplay[i].value =
            opportunityData[outputsToDisplay[i].data_variable_element + "__c"];
        }
        if (
          systemDesignData &&
          outputSettings &&
          outputSettings.source?.includes("SystemDesign")
        ) {
          if (
            outputsToDisplay[i].data_variable_element == "ix_delivery_point"
          ) {
            outputsToDisplay[i].value =
              systemDesignData.ix_delivery_point__c?.trim() || "BTM";
          } else if (
            outputsToDisplay[i].data_variable_element == "es_system_coupling" &&
            !outputsToDisplay[i].value
          ) {
            outputsToDisplay[i].value = systemDesignData.es_system_coupling__c;
          } else {
            if (
              Object.prototype.hasOwnProperty.call(
                systemDesignData,
                outputsToDisplay[i].data_variable_element + "__c",
              )
            ) {
              outputsToDisplay[i].value =
                systemDesignData[
                  outputsToDisplay[i].data_variable_element + "__c"
                ];
            }
          }
        }
        if (
          action === "view" &&
          outputSettings &&
          outputSettings.source === "Pricing Widget"
        ) {
          if (selectedQuoteData) {
            outputsToDisplay[i].value =
              selectedQuoteData[
                outputsToDisplay[i].data_variable_element + "__c"
              ];
          }
          // fillin fields
          const fillInFields = [
            "sr_finl_dev_fee_per_watt_upfront",
            "sr_finl_dev_fee_nominal_upfront",
            "dev_budget_override",
            "dev_budget_exp_adjust_nom",
            "dev_budget_exp_adjust_nom_out",
            "dev_only_expense_budget_total_nom_inp",
            "sr_trans_legal_fees_nom",
            "referral_fees_total_nom",
            "dev_only_expense_budget_total_nom",
            "sr_finl_dev_fee_bess_nom",
            "sr_finl_dev_fee_solar_nom",
            "sr_finl_dev_fee_inv_total_d_1_net_nom",
            "sr_finl_dev_fee_inv_total_d_2_net_nom",
            "sr_finl_dev_fee_inv_total_d_3_net_nom",
            "sr_finl_dev_fee_inv_total_net_nom",
            "sr_finl_dev_fee_inv_total_net_upfr_nom",
            "sr_finl_dev_fee_inv_total_net_def_nom",
            "sr_finl_dev_fee_inv_total_grs_nom",
            "sr_finl_dev_fee_inv_total_adj_nom",
          ]
          fillInFields.forEach((field) => {
            if (
              prevFormData &&
              outputsToDisplay[i].data_variable_element === field
            ) {
              outputsToDisplay[i].value = prevFormData[field];
            }
            });

            // fillin per watt fields
            const perWattFillInFields = {
            "sr_trans_legal_fees_per_watt": "sr_trans_legal_fees_nom",
            "referral_fees_total_per_watt": "referral_fees_total_nom",
            "dev_only_expense_budget_total_per_watt": "dev_only_expense_budget_total_nom",
            }

            Object.entries(perWattFillInFields).forEach(([field, nominalField]) => {
            if (
              prevFormData &&
              outputsToDisplay[i].data_variable_element === field
            ) {
              outputsToDisplay[i].value = pv_size_dc_kw_sd > 0 ? prevFormData[nominalField] / pv_size_dc_kw_sd : 0;
            }
            });
        }

        if (
          outputsToDisplay[i].data_variable_element == "site_lease_solver_type"
        ) {
          if (outputsToDisplay[i].value) {
            if (outputsToDisplay[i].value.toLowerCase() === "upfront") {
              outputsToDisplay[i].value = "Upfront";
            }
            if (outputsToDisplay[i].value.toLowerCase() === "ongoing") {
              outputsToDisplay[i].value = "Ongoing";
            }
          }
        }

        formatValue(outputsToDisplay[i]);
        counter = counter + 1;
      }

      // Filter out outputs that does not satisfy field_values criteria
      const kv_outputsToDisplay = outputsToDisplay.reduce((acc, curr) => {
        acc[curr.data_variable_element] = curr;
        return acc;
      }, {});

      outputsToDisplay = outputsToDisplay.filter((x) => {
        const outputSettingsArr = outputsConstants.filter(
          (y) => y.data_variable_element === x.data_variable_element,
        );
        if (outputSettingsArr.length > 0) {
          // Return true if any outputSettings satisfy the field_values criteria
          return outputSettingsArr.some((outputSettings) => {
            if (outputSettings.field_values) {
              for (const key in outputSettings.field_values) {
                const fieldValue = outputSettings.field_values[key];
                const outputValue = kv_outputsToDisplay[key]?.value;
                if (typeof fieldValue === "function") {
                  if (!fieldValue(outputValue)) {
                    return false;
                  }
                } else if (Array.isArray(fieldValue)) {
                  if (!fieldValue.includes(outputValue)) {
                    return false;
                  }
                } else {
                  if (fieldValue !== outputValue) {
                    return false;
                  }
                }
              }
            }
            return true;
          });
        }
        return true;
      });

      result.push(
        <TableContainer component={Paper}>
          <Table aria-label="dynamic outputs table">
            <TableBody>
              {outputsToDisplay.map((output, index) => (
                <TableRow
                  key={output.ui_name}
                  sx={{
                    "& .MuiTableCell-root": {
                      backgroundColor:
                        index % 2 === 0
                          ? theme.palette.action.hover
                          : theme.palette.background.paper,
                      borderRadius: 0,
                    },
                  }}
                >
                  <TableCell>{output.ui_name}</TableCell>
                  <TableCell align="right">{formatOutput(output)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>,
      );
      const dataToExport = outputsToDisplay.map((x) => {
        const { ui_name, value } = x;
        return { ui_name, value };
      });
      const srDevCostPerWatt = outputsToDisplay.find(
        (x) => x.data_variable_element === "sr_dev_cost_per_watt",
      );
      if (srDevCostPerWatt) {
        srDevCostPerWatt.value = srDevCostPerWatt.value.toFixed(2);
      }
      dataToExport.push({ ui_name: "Offtaker", value: businessName });
      dataToExport.push({ ui_name: "Project ID", value: projectId });
      result.push(renderOutputButtons(dataToExport));
    }

    return <Container>{result}</Container>;
  }, [outputs]);

  function renderOutputButtons(dataToExport) {
    const sharedStyle = {
      background: "linear-gradient(to bottom, #FF7E0B 5%, #FF7E0B 100%)",
      color: "#ffffff",
      fontSize: "15px",
      padding: "7px 10px",
      borderRadius: "4px",
      border: "none",
      textTransform: "uppercase" as const,
      cursor: "pointer",
      transition:
        "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    };

    const leftButtons: ReactJSXElement[] = [];

    leftButtons.push(
      <CsvDownloadButton
        data={dataToExport}
        delimiter=","
        style={sharedStyle}
        filename={"export-" + opportunityData.Id + ".csv"}
      />,
    );

    if (canDownloadModel()) {
      leftButtons.push(
        <button
          style={sharedStyle}
          onClick={() => handleDownloadAWSClicked()}
        >
          Download Model
        </button>,
      );
    }

    const currentSdEpcTotal = systemDesignData?.epc_total_nom_sum__c != null
      ? parseFloat(systemDesignData.epc_total_nom_sum__c)
      : null;
    if (
      action !== "view" &&
      selectedSystemDesign &&
      !outputsSd &&
      optimizedEpcTotalNomSum !== null &&
      currentSdEpcTotal !== null &&
      !isNaN(currentSdEpcTotal) &&
      Math.abs(optimizedEpcTotalNomSum - currentSdEpcTotal) > 0.01
    ) {
      leftButtons.push(
        <button
          style={sharedStyle}
          onClick={() => setNewSdModalOpen(true)}
        >
          New System Design
        </button>,
      );
    }

    return (
      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", marginTop: "20px" }}>
        <div style={{ display: "flex", gap: "10px" }}>
          {leftButtons}
        </div>
        {action !== "view" && (
          <button style={sharedStyle} onClick={() => handleSave()}>
            Save
          </button>
        )}
      </div>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  return (
    <>
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange}>
            <Tab value="1" label="Outputs" />
          </TabList>
        </Box>
        <TabPanel value="1">
          {showOutputs || action === "view" ? (
            renderOutputs
          ) : (
            <p className="pricing-text">
              Fill out the fields on the left to view estimated pricing
            </p>
          )}
        </TabPanel>
      </TabContext>
      {newSdModalOpen && systemDesignData && optimizedEpcTotalNomSum !== null && (
        <NewSystemDesignModal
          open={newSdModalOpen}
          onClose={() => setNewSdModalOpen(false)}
          systemDesignData={systemDesignData}
          optimizedEpcTotal={optimizedEpcTotalNomSum}
          isSDSales={isSDSales}
          onSdCreated={onNewSdCreated}
        />
      )}
    </>
  );
}

export default Outputs;
